'use client';

import { type ChangeEvent, forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { FormFieldContext } from '../form-field/context/form-field.context';
import { SwitchRootContext } from './context/switch-root.context';
import { switchInputStyles } from './styles/switch-input.styles';
import type { SwitchInputPropsInterface } from './types/switch-input.types';

/**
 * @since 10.0.0
 */

export const SwitchInput = forwardRef<HTMLInputElement, SwitchInputPropsInterface>(
  (props: SwitchInputPropsInterface, forwardedRef) => {
    const formFieldContext = useContext(FormFieldContext);
    const switchRootContext = useContext(SwitchRootContext);
    const {
      checked = switchRootContext.checkedState,
      className,
      disabled = switchRootContext.disabled ?? false,
      id = switchRootContext.controlId,
      required = formFieldContext.required ?? false,
      onChange,
      css: cssProp,
      ...rest
    } = props;

    const styles = css(switchInputStyles, cssProp);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      switchRootContext.setCheckedState(e.target.checked);
      onChange?.(e);
    };

    return (
      <input
        checked={checked}
        className={cx('c11n', styles, className)}
        data-c11n-component="Switch.Input"
        data-c11n-version={__C11N_VERSION__}
        disabled={disabled}
        id={id}
        onChange={handleChange}
        ref={forwardedRef}
        required={required}
        role="switch"
        type="checkbox"
        {...rest}
      />
    );
  },
);

SwitchInput.displayName = 'Switch.Input';
